import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Sparkles, Coins, ExternalLink, Loader2 } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { NFTCard } from "@/components/marketplace/NFTCard";
import { useMarketplaceNFTs, MarketplaceNFT } from "@/hooks/useMarketplaceNFTs";
import { supabase } from "@/integrations/supabase/client";
import gameBg from "@/assets/game-bg.png";

export default function Index(): JSX.Element {
  const navigate = useNavigate();
  const { nfts, loading, refresh } = useMarketplaceNFTs();
  const [trending24h, setTrending24h] = useState<Map<string, number>>(new Map());

  // Auto-redirect Supabase password recovery links to the reset page.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const isRecovery =
      (hash && hash.includes("type=recovery")) ||
      (search && search.includes("type=recovery"));
    if (isRecovery) {
      navigate(`/game/reset-password${search}${hash}`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("nft_ownership_history")
        .select("nft_id")
        .gte("created_at", since);
      const counts = new Map<string, number>();
      (data || []).forEach((t: any) => {
        if (t.nft_id) counts.set(t.nft_id, (counts.get(t.nft_id) || 0) + 1);
      });
      setTrending24h(counts);
    })();
  }, []);

  const recent = useMemo(
    () =>
      [...nfts]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10),
    [nfts]
  );
  const trending = useMemo(
    () =>
      [...nfts]
        .map((n) => ({ n, c: trending24h.get(n.id) || 0 }))
        .filter((x) => x.c > 0)
        .sort((a, b) => b.c - a.c)
        .map((x) => x.n)
        .slice(0, 10),
    [nfts, trending24h]
  );
  const mostExpensive = useMemo(
    () =>
      [...nfts]
        .sort((a, b) => (b.current_price || b.price) - (a.current_price || a.price))
        .slice(0, 10),
    [nfts]
  );

  const sections: {
    title: string;
    color: string;
    data: MarketplaceNFT[];
    path: string;
    emptyText: string;
    showTrending?: boolean;
  }[] = [
    { title: "Recent", color: "text-primary", data: recent, path: "/marketplace/recent", emptyText: "No NFTs yet" },
    { title: "Trending (24h)", color: "text-orange-500", data: trending, path: "/marketplace/trending", emptyText: "No sales in the last 24 hours", showTrending: true },
    { title: "Most Expensive", color: "text-yellow-600", data: mostExpensive, path: "/marketplace/most-expensive", emptyText: "No NFTs yet" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader currentPage="home" />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden relative"
      style={{
        backgroundImage: `linear-gradient(hsl(var(--background) / 0.82), hsl(var(--background) / 0.88)), url(${gameBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <AppHeader currentPage="home" />

      <main className="flex-1 pt-28 pb-24 px-3 relative z-10">
        <div className="max-w-6xl mx-auto space-y-4">
          {nfts.length === 0 ? (
            <div className="text-center py-16 space-y-3">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto border border-border">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm font-semibold text-muted-foreground">No NFTs yet</p>
              <Link to="/dashboard">
                <button className="font-bold bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm hover:opacity-90 transition-opacity">
                  Mint the first NFT
                </button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((section) => (
                <div
                  key={section.title}
                  className="rounded-xl p-3 border border-border bg-card/80 backdrop-blur-sm shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h2 className={`text-sm font-extrabold ${section.color} flex items-center gap-1.5`}>
                      {section.title}
                    </h2>
                    <Link
                      to={section.path}
                      className="flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      View All <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                  {section.data.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">{section.emptyText}</p>
                  ) : (
                    <div
                      className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-none"
                      style={{ scrollbarWidth: "none" }}
                    >
                      {section.data.map((nft) => (
                        <div
                          key={nft.id}
                          className="snap-start shrink-0 w-[160px] sm:w-[200px] md:w-[220px]"
                        >
                          <NFTCard nft={nft} showTrending={section.showTrending} onBuySuccess={refresh} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Swap EMON — sempre in fondo */}
          <div className="rounded-xl p-4 border border-primary/30 bg-card/80 backdrop-blur-sm shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <h3 className="text-sm font-bold text-foreground">Top up your account with SOL</h3>
              <p className="text-xs text-muted-foreground">Get EMON instantly on Raydium</p>
            </div>
            <a
              href="https://raydium.io/swap/?inputMint=sol&outputMint=76kfPfv218KhNQGx7gpC1GR9UWLZHBGSimhyK79ZpUMP"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg gradient-primary text-primary-foreground font-semibold text-sm shadow hover:opacity-90 transition-opacity"
            >
              <Coins className="w-4 h-4" />
              Top up with SOL
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
